import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Truck, MapPin, Car, Clock, AlertTriangle, CreditCard, Phone, CheckCircle, Info, Loader2
} from 'lucide-react';
import SystemPayForm from '../components/SystemPayForm';
import { redirectToSystemPay } from '../lib/systempayRedirect';
import AddressAutocomplete from '../components/AddressAutocomplete';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const PAYMENTS_ENABLED = process.env.REACT_APP_PAYMENTS_ENABLED !== 'false';

const INTERVENTION_TYPES = [
  { id: 'depannage_sur_place', name: 'Dépannage sur place', description: 'Réparation sur le lieu de panne' },
  { id: 'remorquage', name: 'Remorquage', description: 'Transport vers un garage' }
];

export default function TowingPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get('cancelled');
  
  const [loading, setLoading] = useState(false);
  const [formToken, setFormToken] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [form, setForm] = useState({
    intervention_type: 'depannage_sur_place',
    vehicle_type: 'auto',
    vehicle_brand: '',
    vehicle_model: '',
    vehicle_plate: '',
    location: '',
    destination: '',
    distance_km: '',
    preferred_date: '',
    preferred_time: '',
    is_night: false,
    description: '',
    phone: user?.phone || ''
  });

  const handlePaid = useCallback(async () => {
    try {
      await fetch(`${API}/towing/confirm-payment?request_id=${requestId}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) { console.error(e); }
    toast.success('Paiement reussi ! Demande confirmee');
    navigate('/mon-compte');
  }, [requestId, token, navigate]);

  const handleFailed = useCallback(() => {
    toast.error('Le paiement a echoue');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Veuillez vous connecter pour réserver');
      navigate('/connexion');
      return;
    }

    if (!form.vehicle_brand || !form.location || !form.preferred_date || !form.preferred_time) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/towing/request`, {
        ...form,
        distance_km: form.distance_km ? parseInt(form.distance_km) : null,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Use formToken for embedded payment
      if (response.data.skip_payment) {
        toast.success('Demande enregistree ! Nous vous contactons rapidement.');
        navigate('/mon-compte');
        return;
      }
      if (response.data.redirect_form) {
        redirectToSystemPay(response.data.redirect_form);
        return;
      }
      if (response.data.formToken) {
        setFormToken(response.data.formToken);
        setRequestId(response.data.request_id);
        setShowPayment(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la demande');
      setLoading(false);
    }
  };

  // Check if time is night (20h-8h)
  const checkNightTime = (time) => {
    if (!time) return false;
    const hour = parseInt(time.split(':')[0]);
    return hour >= 20 || hour < 8;
  };

  const updateForm = (field, value) => {
    const newForm = { ...form, [field]: value };
    if (field === 'preferred_time') {
      newForm.is_night = checkNightTime(value);
    }
    setForm(newForm);
  };

  return (
    <div className="min-h-screen bg-[#09090B] py-8">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-orange-500/10 text-orange-400 px-4 py-2 rounded-full mb-4">
            <Truck size={18} />
            <span className="text-sm font-medium">Dépannage & Remorquage</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Demande d'intervention
          </h1>
          <p className="text-zinc-400">
            Service disponible 7j/7 • Intervention rapide
          </p>
        </div>

        {cancelled && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
            <p className="text-yellow-400">
              <AlertTriangle size={16} className="inline mr-2" />
              Paiement annulé. Votre demande n'a pas été enregistrée.
            </p>
          </div>
        )}

        {/* Deposit Info */}
        <Card className="bg-orange-500/10 border-orange-500/30 mb-6">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <CreditCard className="text-orange-400 mt-1" size={24} />
              <div>
                <h3 className="text-white font-bold mb-1">Frais de déplacement : 80€</h3>
                <p className="text-zinc-300 text-sm">
                  Ce montant sera <strong>déduit de votre facture finale</strong> lors de l'intervention.
                </p>
                <p className="text-orange-400 text-sm mt-2">
                  <AlertTriangle size={14} className="inline mr-1" />
                  <strong>Non remboursable</strong> si annulation moins de 4 heures avant l'intervention.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Détails de l'intervention</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Intervention Type */}
              <div>
                <Label className="text-zinc-300 mb-3 block">Type d'intervention *</Label>
                <div className="grid md:grid-cols-2 gap-3">
                  {INTERVENTION_TYPES.map((type) => (
                    <div
                      key={type.id}
                      onClick={() => updateForm('intervention_type', type.id)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all
                        ${form.intervention_type === type.id 
                          ? 'border-orange-500 bg-orange-500/10' 
                          : 'border-zinc-700 hover:border-zinc-600'}`}
                    >
                      <h3 className="text-white font-bold">{type.name}</h3>
                      <p className="text-zinc-500 text-sm">{type.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Vehicle Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Type de véhicule *</Label>
                  <div className="flex gap-2 mt-2">
                    <Button
                      type="button"
                      variant={form.vehicle_type === 'auto' ? 'default' : 'outline'}
                      className={form.vehicle_type === 'auto' ? 'bg-orange-600' : 'border-zinc-700'}
                      onClick={() => updateForm('vehicle_type', 'auto')}
                    >
                      <Car size={16} className="mr-2" /> Voiture
                    </Button>
                    <Button
                      type="button"
                      variant={form.vehicle_type === 'moto' ? 'default' : 'outline'}
                      className={form.vehicle_type === 'moto' ? 'bg-orange-600' : 'border-zinc-700'}
                      onClick={() => updateForm('vehicle_type', 'moto')}
                    >
                      Moto
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-300">Immatriculation</Label>
                  <Input
                    value={form.vehicle_plate}
                    onChange={(e) => updateForm('vehicle_plate', e.target.value.toUpperCase())}
                    placeholder="AB-123-CD"
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Marque *</Label>
                  <Input
                    required
                    value={form.vehicle_brand}
                    onChange={(e) => updateForm('vehicle_brand', e.target.value)}
                    placeholder="Ex: Peugeot, Renault..."
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Modèle *</Label>
                  <Input
                    required
                    value={form.vehicle_model}
                    onChange={(e) => updateForm('vehicle_model', e.target.value)}
                    placeholder="Ex: 308, Clio..."
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <Label className="text-zinc-300">
                  <MapPin size={14} className="inline mr-1" />
                  Lieu d'intervention * <span className="text-zinc-500 text-xs">(saisie assistee)</span>
                </Label>
                <div className="mt-1">
                  <AddressAutocomplete
                    value={form.location}
                    onChange={(v) => updateForm('location', v)}
                    onSelect={(s) => updateForm('location', s.label)}
                    required
                    placeholder="Adresse complète où se trouve le véhicule"
                    inputClassName="bg-zinc-800 border-zinc-700 text-white pl-10"
                    testid="towing-location"
                  />
                </div>
              </div>

              {form.intervention_type === 'remorquage' && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Destination (pour remorquage) * <span className="text-zinc-500 text-xs">(saisie assistee)</span></Label>
                    <div className="mt-1">
                      <AddressAutocomplete
                        value={form.destination}
                        onChange={(v) => updateForm('destination', v)}
                        onSelect={(s) => updateForm('destination', s.label)}
                        required={form.intervention_type === 'remorquage'}
                        placeholder="Adresse de destination"
                        inputClassName="bg-zinc-800 border-zinc-700 text-white pl-10"
                        testid="towing-destination"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-zinc-300">Distance estimée (km)</Label>
                    <Input
                      type="number"
                      value={form.distance_km}
                      onChange={(e) => updateForm('distance_km', e.target.value)}
                      placeholder="Ex: 15"
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                </div>
              )}

              {/* Date & Time */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">
                    <Clock size={14} className="inline mr-1" />
                    Date souhaitée *
                  </Label>
                  <Input
                    required
                    type="date"
                    value={form.preferred_date}
                    onChange={(e) => updateForm('preferred_date', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Heure souhaitée *</Label>
                  <Input
                    required
                    type="time"
                    value={form.preferred_time}
                    onChange={(e) => updateForm('preferred_time', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                  {form.is_night && (
                    <p className="text-yellow-400 text-xs mt-1">
                      <AlertTriangle size={12} className="inline mr-1" />
                      Intervention de nuit (tarifs majorés)
                    </p>
                  )}
                </div>
              </div>

              {/* Phone */}
              <div>
                <Label className="text-zinc-300">
                  <Phone size={14} className="inline mr-1" />
                  Téléphone pour vous joindre *
                </Label>
                <Input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={(e) => updateForm('phone', e.target.value)}
                  placeholder="06 XX XX XX XX"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>

              {/* Description */}
              <div>
                <Label className="text-zinc-300">Description du problème *</Label>
                <Textarea
                  required
                  value={form.description}
                  onChange={(e) => updateForm('description', e.target.value)}
                  placeholder="Décrivez la panne ou le problème rencontré..."
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  rows={3}
                />
              </div>

              {/* Terms */}
              <div className="bg-zinc-800/50 rounded-lg p-4 text-sm">
                <p className="text-zinc-400 mb-2">
                  <Info size={14} className="inline mr-1" />
                  En validant cette demande :
                </p>
                <ul className="text-zinc-500 space-y-1 ml-5 list-disc">
                  <li>Vous acceptez de payer les frais de déplacement de <strong className="text-white">80€</strong></li>
                  <li>Ce montant sera <strong className="text-green-400">déduit de votre facture finale</strong></li>
                  <li>Annulation gratuite jusqu'à <strong className="text-white">4h avant</strong> l'intervention</li>
                  <li><strong className="text-orange-400">Aucun remboursement</strong> après ce délai</li>
                </ul>
              </div>

              {!user && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <p className="text-yellow-400 text-sm">
                    <AlertTriangle size={16} className="inline mr-2" />
                    Vous devez être connecté pour effectuer une demande.
                    <Button 
                      variant="link" 
                      className="text-yellow-400 p-0 ml-1"
                      onClick={() => navigate('/connexion')}
                    >
                      Se connecter
                    </Button>
                  </p>
                </div>
              )}

              <Button 
                type="submit"
                disabled={loading || !user || showPayment}
                className="w-full bg-orange-600 hover:bg-orange-700 py-6 text-lg"
                data-testid="towing-submit-pay"
              >
                <CreditCard size={20} className="mr-2" />
                {loading ? <><Loader2 className="animate-spin mr-2" size={20} /> Préparation...</> : (PAYMENTS_ENABLED ? 'Payer 80€ et confirmer la demande' : 'Confirmer la demande (acompte 80€ a regler en magasin)')}
              </Button>

              {/* SystemPay Payment */}
              {showPayment && formToken && (
                <div className="mt-6 p-6 bg-zinc-800/50 rounded-xl border border-zinc-700">
                  <h3 className="text-white font-bold text-lg mb-4 flex items-center justify-center gap-2">
                    <CreditCard className="text-orange-500" size={20} />
                    Paiement de l'acompte - 80 EUR
                  </h3>
                  <SystemPayForm
                    formToken={formToken}
                    amount={80}
                    onPaid={handlePaid}
                    onFailed={handleFailed}
                    testId="systempay-towing-form"
                  />
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
